import * as Yup from "yup";
import moment from "moment";
import {
  INVOICE_NUMBER_INVALID_CHARS_MSG,
  INVOICE_NUMBER_REGEX,
} from "../../../constants/invoiceNumberPattern";

// Valid status codes from the backend
const VALID_STATUSES = [
  "P",
  "R",
  "V",
  "W",
  "S",
  "C",
  "Q",
  "F",
  "I",
  "T",
  "Z",
  "E",
  "L",
  "O",
  "X",
  "Y",
];

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/**
 * API → form (DateField uses YYYYMMDD). Uses date portion only to avoid timezone shifts.
 */
export const parseExpiresAtFromApi = (apiValue) => {
  if (apiValue == null || apiValue === "") {
    return "";
  }
  const datePart = String(apiValue).slice(0, 10);
  if (!ISO_DATE_PREFIX.test(datePart)) {
    return "";
  }
  return datePart.replace(/-/g, "");
};

/**
 * Form → API. Returns YYYY-MM-DD (or "") — Legacy invoice endpoint rejects YYYYMMDD.
 * DateField keeps YYYYMMDD in the form; we convert only on submit.
 */
export const formatExpiresAtForApi = (formValue, noExpiration) => {
  if (noExpiration) {
    return "";
  }
  if (formValue == null || formValue === "") {
    return "";
  }
  const raw = String(formValue).trim();
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  if (ISO_DATE_PREFIX.test(raw)) {
    const datePart = raw.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }
  return "";
};

/** YYYY-MM-DD string for the API (or "" to clear / no expiration). */
export const toExpiresAtPayloadString = (formValue, noExpiration) =>
  String(formatExpiresAtForApi(formValue, noExpiration));

/** Same format for FormData append in thunks. */
export const toExpiresAtFormDataValue = (expiresAt) => {
  if (expiresAt === undefined || expiresAt === null) {
    return "";
  }
  return String(formatExpiresAtForApi(expiresAt, false));
};

/**
 * List/display: date portion only (MM/DD/YYYY), no timezone conversion.
 */
export const formatExpiresAtForDisplay = (apiValue) => {
  if (apiValue == null || apiValue === "") {
    return "-";
  }
  const datePart = String(apiValue).slice(0, 10);
  if (!ISO_DATE_PREFIX.test(datePart)) {
    return "-";
  }
  const [year, month, day] = datePart.split("-");
  return `${month}/${day}/${year}`;
};

const isValidExpiresAtFormValue = (value) => {
  if (value == null || value === "") {
    return true;
  }
  const raw = String(value).trim();
  if (/^\d{8}$/.test(raw)) {
    return moment(raw, "YYYYMMDD", true).isValid();
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return moment(raw, "YYYY-MM-DD", true).isValid();
  }
  return false;
};

export const invoiceInitialValues = {
  invoice_number: "",
  amount: "",
  currency: "",
  status: "P",
  client_info_id: null,
  invoice_file: null,
  expires_at: "",
  no_expiration: true,
};

const currencyFieldSchema = (merchantCurrency, isCreate) => {
  let schema = Yup.string()
    .required("Currency is required")
    .test(
      "not-empty",
      "Currency cannot be empty",
      (value) => value && value.trim().length > 0,
    )
    .test(
      "length-3",
      "Currency must be exactly 3 characters",
      (value) => value && value.trim().length === 3,
    )
    .transform((value) => (value ? value.toUpperCase() : value));

  if (isCreate && merchantCurrency) {
    schema = schema.oneOf(
      [merchantCurrency.toUpperCase()],
      "Currency must match merchant configuration",
    );
  }

  return schema;
};

export const buildInvoiceValidationSchema = (merchantCurrency, isCreate) =>
  Yup.object().shape({
  invoice_number: Yup.string()
    .required("Invoice number is required")
    .max(100, "Invoice number cannot exceed 100 characters")
    .matches(INVOICE_NUMBER_REGEX, INVOICE_NUMBER_INVALID_CHARS_MSG)
    .test(
      "not-empty",
      "Invoice number cannot be empty",
      (value) => value && value.trim().length > 0
    ),

  amount: Yup.number()
    .required("Amount is required")
    .min(0, "Amount must be greater than or equal to 0")
    .typeError("Amount must be a valid number")
    .transform((value, originalValue) => {
      // Handle empty string or undefined
      if (originalValue === "" || originalValue == null) {
        return undefined;
      }
      // Convert string to number
      const num = Number(originalValue);
      return isNaN(num) ? originalValue : num;
    }),

  currency: currencyFieldSchema(merchantCurrency, isCreate),

  status: Yup.string()
    .required("Status is required")
    .test(
      "length-1",
      "Status must be exactly 1 character",
      (value) => value && value.trim().length === 1
    )
    .oneOf(
      VALID_STATUSES,
      `Status must be one of: ${VALID_STATUSES.join(", ")}`
    )
    .transform((value) => (value ? value.toUpperCase() : value)),

  client_info_id: Yup.number()
    .required("Client Info ID is required")
    .integer("Client Info ID must be an integer")
    .positive("Client Info ID must be positive"),

  no_expiration: Yup.boolean(),

  expires_at: Yup.string().when("no_expiration", {
    is: false,
    then: (schema) =>
      schema.test(
        "valid-expires-at",
        "Use a valid expiration date",
        (value) => isValidExpiresAtFormValue(value),
      ),
    otherwise: (schema) => schema.notRequired(),
  }),

  invoice_file: Yup.mixed()
    .nullable()
    .test(
      "fileType",
      "Only PDF files are allowed",
      (value) => {
        if (!value || !(value instanceof File)) return true;
        return (
          value.type === "application/pdf" ||
          value.name.toLowerCase().endsWith(".pdf")
        );
      }
    )
    .test(
      "fileSize",
      "The file size must be less than 10MB",
      (value) => {
        if (!value || !(value instanceof File)) return true;
        const maxSize = 10 * 1024 * 1024; // 10MB
        return value.size <= maxSize;
      }
    ),
  });

/** @deprecated Use buildInvoiceValidationSchema(merchantCurrency, isCreate) */
export const invoiceInitialValidationSchema = buildInvoiceValidationSchema(
  "USD",
  true,
);

export const setInvoiceValues = ({ invoiceData, setFieldValue }) => {
  setFieldValue("invoice_number", invoiceData.invoice_number || "", true);
  setFieldValue("amount", invoiceData.amount || "", true);
  setFieldValue("currency", invoiceData.currency || "", true);
  setFieldValue("status", invoiceData.status || "P", true);
  setFieldValue("client_info_id", invoiceData.client_info_id || null, true);
  setFieldValue("invoice_file", null, true);

  const expiresAtForm = parseExpiresAtFromApi(invoiceData.expires_at);
  setFieldValue("expires_at", expiresAtForm, true);
  setFieldValue("no_expiration", !expiresAtForm, true);
};
